import { DateTime } from "luxon";

import LocalForagePlugin, { sectors, syncState } from "@/plugins/LocalForagePlugin";
import { sectorsSyncKey } from "@/types/products/product-keys";

import { batchSetItems, batchRemoveItems } from "@/utils/localforage-batch";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";

import type { ContentfulSectors, SectorContentfulInfo } from "@tmi/types/api/content-models";
import type { ContentfulSector } from "@tmi/types/entities";
import type { Sector, GetSectorsResponse } from "@tmi/types/api/market-data";

import { createDefaultLogger, LogLevels } from "@/types/console-logger";

const logger = createDefaultLogger( LogLevels.DEBUG );

export async function refreshSectorsFromServer( authToken: string | null ): Promise<ContentfulSector[]> {
    let allSectors: ContentfulSector[] = [];

    const t0: number = performance.now();

    const getTmiSectorsResponse = await goFetch<GetSectorsResponse, PersistedQueryPayload>( {
        serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
        endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
        payload: {
            id: 'ec7783c18b91cc181eead26d47c0a14f'
        },
        authToken: authToken
    } );

    const tmiSectors = new Map<string, ContentfulSector>();

    if ( null != getTmiSectorsResponse?.data?.sectors?.items ) {
        getTmiSectorsResponse.data.sectors.items.forEach( ( node: Sector ) => {
            tmiSectors.set( node.symbol.toLowerCase(),
                {
                    id: node.id,
                    name: node.name,
                    symbol: node.symbol,
                    // Data that comes from Contentful, but provide default values (useful when the sector hasn't been defined in Contentful yet)
                    slug: node.symbol.toLowerCase(),
                    contentfulId: node.symbol.toLowerCase(),
                } );
        } );

        let cmsSectors: ContentfulSector[] | undefined = [];
        let page: number = 0;
        const pageSize: number = 50;

        while ( true ) {
            const response =
                await goFetch<ContentfulSectors, PersistedQueryPayload>( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: 'e82f7b992a593849eea4503aa3f594b9',
                        variables: {
                            skip: page,
                            limit: pageSize
                        }
                    },
                    authToken: authToken
                } );

            // If no response or no data, stop pagination
            const currentPageSectors = response?.data?.sectorCatalog?.sectors;
            if ( !currentPageSectors || currentPageSectors.length === 0 ) {
                break;
            }

            // Process the sectors from this page
            cmsSectors = [ ...( response.data.sectorCatalog.sectors
                    .map( ( info: SectorContentfulInfo ): ContentfulSector =>
                        ( {
                            contentfulId: info.id,
                            name: info.name,
                            slug: info.slug.toLowerCase(),
                            symbol: info.short_name,
                            // Data that comes from TMI/II
                            id: 0
                        } ) )
            ) ];

            cmsSectors.forEach( ( contentfulSector: ContentfulSector ) => {
                const tmiSector: ContentfulSector | undefined = tmiSectors.get( contentfulSector.symbol.toLowerCase() );

                if ( tmiSector ) {
                    tmiSector.slug = contentfulSector.slug;
                    tmiSector.contentfulId = contentfulSector.contentfulId;
                }
            } );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ currentPageSectors.length } sector items (page ${ page }) took ${ t1 - t0 } ms` );

            // Stop if this page had fewer items than pageSize (last page)
            if ( currentPageSectors.length < pageSize ) {
                break;
            }

            page++;
        }

        allSectors = Array.from( tmiSectors, ( [ _, value ] ) => value );
    }

    return allSectors;
}

export async function loadSectorMaps( sectorByContentfulIdMap: Map<string, ContentfulSector>,
                                      sectorByTmiIdMap: Map<number, ContentfulSector>,
                                      authToken: string | null ): Promise<boolean> {
    let updated = false;
    try {
        const t0: number = performance.now();

        // Verify that IDB has been initialized
        if ( typeof sectors === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            await LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem<number>( sectorsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            const sectorsReceived: ContentfulSector[] = await refreshSectorsFromServer( authToken );

            if ( sectorsReceived.length > 0 ) {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
                await syncState.setItem( sectorsSyncKey, nextSyncThresholdInMillis );
                updated = true;

                await sectors.ready();

                // Purge IDB entries that no longer exist on the server
                const receivedIds = new Set( sectorsReceived.map( s => s.contentfulId ) );
                const keysToRemove: string[] = [];
                await sectors.iterate( ( _: ContentfulSector, key: string ) => {
                    if ( !receivedIds.has( key ) ) { keysToRemove.push( key ); }
                    return;
                } );
                await batchRemoveItems( sectors, keysToRemove );

                await batchSetItems( sectors, sectorsReceived.map( s => [ s.contentfulId, s ] as const ) );

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ sectorsReceived.length } sectors from server in ${ t1 - t0 } ms (${ keysToRemove.length } stale removed)` );

                sectorByContentfulIdMap.clear();
                sectorByTmiIdMap.clear();
                sectorsReceived.forEach( s => {
                    sectorByContentfulIdMap.set( s.contentfulId, s );
                    sectorByTmiIdMap.set( s.id, s );
                } );
                return updated;
            } else {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( sectorsSyncKey, nextSyncThresholdInMillis );
            }
        } else if ( sectorByContentfulIdMap.size > 0 || sectorByTmiIdMap.size > 0 ) {
            return updated;
        }

        // Cold start: populate maps from IDB cache
        sectorByTmiIdMap.clear();
        sectorByContentfulIdMap.clear();
        await sectors.ready();
        await sectors.iterate( ( sector: ContentfulSector ) => {
            sectorByContentfulIdMap.set( sector.contentfulId, sector );
            sectorByTmiIdMap.set( sector.id, sector );
            return;
        } );

    } catch ( err: any ) {
        logger.error( '[ sector-sync-coordinator ] Sync failed:', err );
    }

    return updated;
}